// ============================================================================
// FILE:    src/app/core/auth/role-capabilities.map.ts
// VERSION: v2.0.0 — RBAC Phase 2
// DATE:    2026-05-09
//
// ZWECK:
//   DIE EINZIGE STELLE wo definiert wird welche Rolle welche Capabilities hat.
//   Alle Guards, Direktiven und Services lesen aus diesen Maps.
//   Rollen-Umstrukturierung → NUR HIER ändern, nirgendwo sonst.
//
// AUFBAU:
//   PROJECT_ROLE_CAPABILITIES: Projekt-Rollen → Capabilities (pslot-bezogen)
//   ORG_ROLE_CAPABILITIES:     Org-Rollen → Capabilities (tenant-weit)
//
// KUMULATIV-PRINZIP:
//   Jede höhere Rolle enthält ALLE Capabilities der niedrigeren Rollen.
//   Implementiert als explizite Listen (kein Spread von niedrigerer Rolle),
//   damit jede Rolle selbstdokumentierend und überschaubar bleibt.
//
// WARUM PROJECT_VIEWER kein DAILY_VIEW hat:
//   Der Daily-Bereich ist der interne Team-Arbeitsraum. Externe Stakeholder
//   (Viewer) sollen die tägliche Team-Kommunikation nicht mitlesen — das
//   schützt psychologische Sicherheit und offene Kommunikation im Team.
//
// WARUM PROJECT_MEMBER kein SPRINT_CREATE / ITEM_CREATE hat:
//   Sprint-Planung und Backlog-Management sind Planungsverantwortung des
//   IT-PM (PO/SM-Rolle). Ein Developer der Sprints erstellt untergräbt den
//   definierten Planungsprozess.
//
// WARUM PROJECT_IT_PM kein PROJECT_ACCESS_MANAGE hat:
//   Wer im Projekt planen darf ≠ wer das Projekt administriert.
//   Access Management (wer darf rein, in welcher Rolle) ist Admin-Aufgabe.
//   IT-PM plant den Inhalt, Admin verwaltet das Team.
// ============================================================================

import { Role } from './roles.enum';
import { Capability } from './capabilities.enum';

// ── Projekt-Rollen → Capabilities ────────────────────────────────────────────

export const PROJECT_ROLE_CAPABILITIES: Record<string, Capability[]> = {

  // PROJECT_IDLE: kein Zugang → keine Capabilities
  // Einzige erreichbare Page: /project/:pslot/request-access (requires: [] in page-access.config)
  [Role.PROJECT_IDLE]: [],

  // PROJECT_VIEWER: Nur lesen — externe Stakeholder, Kunden, Auditoren
  // KEIN Daily (Team-Arbeitsraum bleibt intern)
  // KEIN Task/Item-Edit, KEIN RT-Assessment (Viewer ist kein Team-Mitglied)
  [Role.PROJECT_VIEWER]: [
    Capability.SPRINT_VIEW,
    Capability.DASHBOARD_VIEW,
    Capability.ITEM_VIEW,
    Capability.TASK_SEEK,
    Capability.ITEM_SEEK,
    Capability.PERSONA_VIEW,
    Capability.WBS_VIEW,
  ],

  // PROJECT_MEMBER: Ausführendes Teammitglied — Developer, Tester, BA/RE Ausführung
  // + Daily (Team-Mitglied füllt eigenen Daily aus)
  // + Task erstellen/bearbeiten (eigene Arbeit organisieren)
  // + RT Assessment (persönlicher Team-Test, macht für Mitglieder Sinn)
  // KEIN Sprint/Item/Milestone/Persona/Solution Create (Planungsartefakte → IT-PM)
  [Role.PROJECT_MEMBER]: [
    Capability.SPRINT_VIEW,
    Capability.DASHBOARD_VIEW,
    Capability.ITEM_VIEW,
    Capability.ITEM_EDIT_OWN,
    Capability.TASK_SEEK,
    Capability.ITEM_SEEK,
    Capability.TASK_CREATE,
    Capability.TASK_EDIT_OWN,
    Capability.PERSONA_VIEW,
    Capability.DAILY_VIEW,
    Capability.DAILY_OWN_EDIT,
    Capability.RT_ASSESSMENT_OWN,
    Capability.WBS_VIEW,
  ],

  // PROJECT_IT_PM: Planungsrolle — PO, SM, BA/RE Author, Product Manager
  // + alle Member-Capabilities
  // + Sprint/Item/Milestone/Persona/Solution CRUD (Planungsverantwortung)
  // KEIN PROJECT_ACCESS_MANAGE (Admin-Aufgabe, nicht Planungsaufgabe)
  [Role.PROJECT_IT_PM]: [
    Capability.SPRINT_VIEW,
    Capability.SPRINT_CREATE,
    Capability.SPRINT_EDIT,
    Capability.DASHBOARD_VIEW,
    Capability.ITEM_VIEW,
    Capability.ITEM_CREATE,
    Capability.ITEM_EDIT_OWN,
    Capability.TASK_SEEK,
    Capability.ITEM_SEEK,
    Capability.TASK_CREATE,
    Capability.TASK_EDIT_OWN,
    Capability.MILESTONE_CREATE,
    Capability.MILESTONE_EDIT,
    Capability.PERSONA_VIEW,
    Capability.PERSONA_MANAGE,
    Capability.SOLUTION_CREATE,
    Capability.SOLUTION_EDIT,
    Capability.DAILY_VIEW,
    Capability.DAILY_OWN_EDIT,
    Capability.RT_ASSESSMENT_OWN,
    Capability.WBS_VIEW,
    Capability.WBS_MANAGE,
  ],

  // PROJECT_PLANNER: SCOPE Team-Planung (orthogonal zu Solution Author)
  // = Member-Basics + Item/Sprint/Milestone CRUD.
  // BEWUSST KEIN Solution/Persona-Manage (das ist der Solution-Author-Scope).
  [Role.PROJECT_PLANNER]: [
    Capability.SPRINT_VIEW,
    Capability.SPRINT_CREATE,
    Capability.SPRINT_EDIT,
    Capability.DASHBOARD_VIEW,
    Capability.ITEM_VIEW,
    Capability.ITEM_CREATE,
    Capability.ITEM_EDIT_OWN,
    Capability.TASK_SEEK,
    Capability.ITEM_SEEK,
    Capability.TASK_CREATE,
    Capability.TASK_EDIT_OWN,
    Capability.MILESTONE_CREATE,
    Capability.MILESTONE_EDIT,
    Capability.PERSONA_VIEW,
    Capability.DAILY_VIEW,
    Capability.DAILY_OWN_EDIT,
    Capability.RT_ASSESSMENT_OWN,
    Capability.WBS_VIEW,
    Capability.WBS_MANAGE,
  ],

  // PROJECT_SOLUTION_AUTHOR: SCOPE SAFe-Elemente (orthogonal zu Planner)
  // = Member-Basics + Solution/Persona-Manage.
  // BEWUSST KEIN Item/Sprint/Milestone-Create (das ist der Planner-Scope).
  [Role.PROJECT_SOLUTION_AUTHOR]: [
    Capability.SPRINT_VIEW,
    Capability.DASHBOARD_VIEW,
    Capability.ITEM_VIEW,
    Capability.ITEM_EDIT_OWN,
    Capability.TASK_SEEK,
    Capability.ITEM_SEEK,
    Capability.TASK_CREATE,
    Capability.TASK_EDIT_OWN,
    Capability.PERSONA_VIEW,
    Capability.PERSONA_MANAGE,
    Capability.SOLUTION_CREATE,
    Capability.SOLUTION_EDIT,
    Capability.DAILY_VIEW,
    Capability.DAILY_OWN_EDIT,
    Capability.RT_ASSESSMENT_OWN,
    Capability.WBS_VIEW,
  ],

  // PROJECT_ADMIN: Projekt-Administration — alles von IT-PM + Access Management
  // Gatekeeper: wer darf in welcher Rolle ins Projekt
  [Role.PROJECT_ADMIN]: [
    Capability.SPRINT_VIEW,
    Capability.SPRINT_CREATE,
    Capability.SPRINT_EDIT,
    Capability.DASHBOARD_VIEW,
    Capability.ITEM_VIEW,
    Capability.ITEM_CREATE,
    Capability.ITEM_EDIT_OWN,
    Capability.TASK_SEEK,
    Capability.ITEM_SEEK,
    Capability.TASK_CREATE,
    Capability.TASK_EDIT_OWN,
    Capability.MILESTONE_CREATE,
    Capability.MILESTONE_EDIT,
    Capability.PERSONA_VIEW,
    Capability.PERSONA_MANAGE,
    Capability.SOLUTION_CREATE,
    Capability.SOLUTION_EDIT,
    Capability.DAILY_VIEW,
    Capability.DAILY_OWN_EDIT,
    Capability.RT_ASSESSMENT_OWN,
    Capability.WBS_VIEW,
    Capability.WBS_MANAGE,
    Capability.PROJECT_ACCESS_MANAGE,  // ← das Unterscheidungsmerkmal
    Capability.THIRD_PARTY_CONFIG,
  ],
};

// ── Org-Rollen → Capabilities ─────────────────────────────────────────────────

export const ORG_ROLE_CAPABILITIES: Record<string, Capability[]> = {

  // NONE: kein Zugang
  [Role.NONE]: [],

  // APP_USER: normaler User — nur persönliche Funktionen org-weit
  // Projekt-Capabilities kommen aus PROJECT_ROLE_CAPABILITIES (pslot-bezogen)
  [Role.APP_USER]: [
    Capability.OWN_PROFILE_VIEW,
    Capability.RT_ASSESSMENT_OWN,
  ],

  // PMO_OFFICER: App-Management komplett
  // Eine Person die alles Organisatorische für das Team regelt.
  // Hat zusätzlich PROJECT_ACCESS_MANAGE für alle Projekte (org-weit),
  // damit er ohne eigenen Projekt-Zugang dennoch User einrichten kann.
  [Role.PMO_OFFICER]: [
    Capability.OWN_PROFILE_VIEW,
    Capability.RT_ASSESSMENT_OWN,
    Capability.APP_MANAGEMENT_ACCESS,
    Capability.USER_PROVISION,
    Capability.THIRD_PARTY_CONFIG,
    Capability.PROJECT_ACCESS_MANAGE,  // org-weiter Bypass für Projekt-Access-Mgmt
  ],

  // SUPER_ADMIN: alle Capabilities — wird in CapabilityService via isSuperAdmin
  // als vollständiger Bypass implementiert (new Set(Object.values(Capability)))
  [Role.SUPER_ADMIN]: Object.values(Capability) as Capability[],
};
